import { useRef, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Loader2, Brush, Eraser, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { uploadAsset, inpaintImage } from "@/lib/falApi";

const cls = (...a) => a.filter(Boolean).join(" ");

export default function InpaintEditor({ open, imageUrl, onClose, onDone }) {
  const imgRef = useRef(null);
  const overlayRef = useRef(null);
  const maskRef = useRef(null);
  const drawing = useRef(false);
  const painted = useRef(false);
  const [brush, setBrush] = useState(44);
  const [erase, setErase] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => { if (!open) { setReady(false); setPrompt(""); setErase(false); painted.current = false; } }, [open]);

  const initCanvases = () => {
    const img = imgRef.current;
    if (!img) return;
    const dw = img.clientWidth, dh = img.clientHeight;
    const ov = overlayRef.current;
    ov.width = dw; ov.height = dh;
    ov.getContext("2d").clearRect(0, 0, dw, dh);
    const m = document.createElement("canvas");
    m.width = img.naturalWidth; m.height = img.naturalHeight;
    const mctx = m.getContext("2d");
    mctx.fillStyle = "#000"; mctx.fillRect(0, 0, m.width, m.height);
    maskRef.current = m;
    painted.current = false;
    setReady(true);
  };

  const posOf = (e) => {
    const r = overlayRef.current.getBoundingClientRect();
    return {
      x: (e.touches?.[0]?.clientX ?? e.clientX) - r.left,
      y: (e.touches?.[0]?.clientY ?? e.clientY) - r.top,
    };
  };

  const paint = (e) => {
    if (!drawing.current || !ready) return;
    e.preventDefault();
    const { x, y } = posOf(e);
    const ov = overlayRef.current, octx = ov.getContext("2d");
    const scale = maskRef.current.width / ov.width;
    const mctx = maskRef.current.getContext("2d");
    if (erase) {
      octx.globalCompositeOperation = "destination-out";
      octx.beginPath(); octx.arc(x, y, brush / 2, 0, Math.PI * 2); octx.fill();
      octx.globalCompositeOperation = "source-over";
      mctx.fillStyle = "#000";
      mctx.beginPath(); mctx.arc(x * scale, y * scale, (brush / 2) * scale, 0, Math.PI * 2); mctx.fill();
    } else {
      octx.fillStyle = "rgba(250,255,105,0.5)";
      octx.beginPath(); octx.arc(x, y, brush / 2, 0, Math.PI * 2); octx.fill();
      mctx.fillStyle = "#fff";
      mctx.beginPath(); mctx.arc(x * scale, y * scale, (brush / 2) * scale, 0, Math.PI * 2); mctx.fill();
      painted.current = true;
    }
  };

  const clearMask = () => {
    if (!ready) return;
    const ov = overlayRef.current; ov.getContext("2d").clearRect(0, 0, ov.width, ov.height);
    const m = maskRef.current, mctx = m.getContext("2d");
    mctx.fillStyle = "#000"; mctx.fillRect(0, 0, m.width, m.height);
    painted.current = false;
  };

  const run = async () => {
    if (!painted.current) { toast.error("Paint over the area you want to change first"); return; }
    if (!prompt.trim()) { toast.error("Describe what should fill the painted area"); return; }
    setBusy(true);
    try {
      const blob = await new Promise((r) => maskRef.current.toBlob(r, "image/png"));
      const file = new File([blob], "mask.png", { type: "image/png" });
      const up = await uploadAsset(file);
      const rec = await inpaintImage(imageUrl, up.url, prompt.trim());
      toast.success("Inpainted — saved to gallery");
      onDone?.(rec);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Inpaint failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-2xl" data-testid="inpaint-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Brush className="h-5 w-5 text-[#faff69]" /> Inpaint — paint the area to change</DialogTitle>
          <DialogDescription className="text-zinc-400">Brush over the region you want regenerated, then describe what should go there. Powered by FLUX Fill.</DialogDescription>
        </DialogHeader>

        <div className="relative grid place-items-center bg-black rounded-md p-2 max-h-[50vh] overflow-hidden select-none">
          <img ref={imgRef} src={imageUrl} alt="inpaint source" crossOrigin="anonymous" onLoad={initCanvases}
            className="max-h-[46vh] object-contain pointer-events-none" data-testid="inpaint-image" draggable={false} />
          <canvas ref={overlayRef} data-testid="inpaint-canvas"
            className="absolute cursor-crosshair touch-none"
            style={{ left: "50%", top: "50%", transform: "translate(-50%,-50%)" }}
            onMouseDown={(e) => { drawing.current = true; paint(e); }}
            onMouseMove={paint}
            onMouseUp={() => { drawing.current = false; }}
            onMouseLeave={() => { drawing.current = false; }}
            onTouchStart={(e) => { drawing.current = true; paint(e); }}
            onTouchMove={paint}
            onTouchEnd={() => { drawing.current = false; }} />
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => setErase(false)} data-testid="inpaint-brush"
            className={cls("flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs border transition-colors",
              !erase ? "border-[#faff69] text-[#faff69] bg-[#faff69]/10" : "border-zinc-800 text-zinc-400 hover:text-white")}>
            <Brush className="h-3.5 w-3.5" /> Paint
          </button>
          <button onClick={() => setErase(true)} data-testid="inpaint-erase"
            className={cls("flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs border transition-colors",
              erase ? "border-[#faff69] text-[#faff69] bg-[#faff69]/10" : "border-zinc-800 text-zinc-400 hover:text-white")}>
            <Eraser className="h-3.5 w-3.5" /> Erase
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-[140px]">
            <span className="text-[11px] font-mono text-zinc-500">brush</span>
            <Slider value={[brush]} min={10} max={120} step={2} onValueChange={(v) => setBrush(v[0])} data-testid="inpaint-brush-size" />
            <span className="text-[11px] font-mono text-zinc-400 w-8">{brush}</span>
          </div>
          <button onClick={clearMask} data-testid="inpaint-clear" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs border border-zinc-800 text-zinc-400 hover:text-white transition-colors">
            <RotateCcw className="h-3.5 w-3.5" /> Clear
          </button>
        </div>

        <Input data-testid="inpaint-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)}
          placeholder="What should fill the painted area? e.g. 'a red baseball cap'"
          className="bg-[#0a0a0a] border-zinc-800 focus-visible:ring-[#faff69] text-sm" />

        <DialogFooter>
          <Button variant="ghost" className="text-zinc-400" onClick={() => onClose?.()} data-testid="inpaint-cancel">Cancel</Button>
          <Button onClick={run} disabled={busy} className="bg-[#faff69] hover:bg-[#e6eb52] text-black" data-testid="inpaint-run">
            {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Filling…</> : <><Sparkles className="h-4 w-4 mr-1.5" /> Generate fill</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
